import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getPortalData, payCharge, reportIssue, sendPortalMessage, toggleSavedAlerts, requestRenewal, requestCallback, respondToOffer, downloadLandlordStatement, toggleWatchlist } from "../api";
import { useToast } from "../hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Building2, CreditCard, Wrench, FileText, MessageSquare, ClipboardCheck,
  TrendingUp, CalendarClock, Heart, Home, Plus, Check, FileDown, Phone, RefreshCw, X, AlertTriangle,
} from "lucide-react";
import PortalCompliance from "../components/portal/PortalCompliance";
import PortalServices from "../components/portal/PortalServices";
import PortalPhotography from "../components/portal/PortalPhotography";
import LandlordCompliance from "../components/portal/LandlordCompliance";
import LandlordProtection from "../components/portal/LandlordProtection";
import {
  fmt, fmtDate, SectionCard, StageTimeline, DocumentsList, InvoicesList,
  MessagesThread,
} from "../components/portal/PortalShared";

const Portal = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [issueForm, setIssueForm] = useState({ title: "", detail: "", priority: "medium" });
  const [showIssue, setShowIssue] = useState(false);

  const refresh = useCallback(() => {
    getPortalData().then(setData).catch(() => setData(null));
  }, []);

  const saveOpp = async (propertyId) => {
    try {
      const r = await toggleWatchlist(propertyId);
      setData((d) => {
        if (!d) return d;
        const opps = (d.opportunities || []).map((o) => o.id === propertyId ? { ...o, saved: r.saved } : o);
        const watchlist = opps.filter((o) => o.saved);
        const alerts = (d.alerts || []).filter((a) => !(a.id === propertyId && r.saved));
        return { ...d, opportunities: opps, watchlist, alerts,
          investorStats: { ...d.investorStats, watchlist: r.watchlist.length } };
      });
    } catch { toast({ title: "Couldn't update watchlist" }); }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8a8a8a] font-display text-[20px]">Loading...</div>;
  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-display text-[30px] text-[#2b2b2b] mb-2">Sign in to your portal</h1>
        <p className="text-[14px] text-[#8a8a8a] mb-6 max-w-[420px]">Access your dashboard for viewings, documents, payments, offers and more.</p>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Link to="/login?redirect=/portal" data-testid="portal-signin-cta" className="px-7 py-3 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">Sign in</Link>
          <Link to="/" className="px-7 py-3 rounded-full bg-white border border-[#e2d8c8] text-[#6a6a6a] text-[14px] font-500 hover:border-[#b8935f] transition-colors">Back to Home</Link>
        </div>
      </div>
    );
  }

  const handlePay = async (id) => {
    try { await payCharge(id); toast({ title: "Payment recorded", description: "Awaiting agent confirmation." }); refresh(); }
    catch { toast({ title: "Couldn't record payment" }); }
  };
  const handleIssue = async (e) => {
    e.preventDefault();
    if (!issueForm.title) return;
    try { await reportIssue(issueForm); toast({ title: "Issue reported", description: "We'll be in touch shortly." }); setIssueForm({ title: "", detail: "", priority: "medium" }); setShowIssue(false); refresh(); }
    catch { toast({ title: "Couldn't submit issue" }); }
  };
  const handleSend = async (text) => {
    try { await sendPortalMessage(text); refresh(); } catch { toast({ title: "Couldn't send message" }); }
  };
  const handleToggleAlerts = async (id, alerts) => {
    try { await toggleSavedAlerts(id, alerts); refresh(); toast({ title: alerts ? "Email alerts on" : "Email alerts off" }); }
    catch { toast({ title: "Couldn't update alerts" }); }
  };
  const handleRenewal = async () => {
    try { await requestRenewal(); toast({ title: "Renewal requested", description: "Your agent will send terms shortly." }); refresh(); }
    catch { toast({ title: "Couldn't send request" }); }
  };
  const handleCallback = async () => {
    try { await requestCallback(); toast({ title: "Call-back booked", description: "An agent will ring you shortly." }); refresh(); }
    catch { toast({ title: "Couldn't request call-back" }); }
  };
  const handleOffer = async (index, decision) => {
    try { await respondToOffer(index, decision); toast({ title: decision === "accept" ? "Offer accepted" : "Offer declined" }); refresh(); }
    catch { toast({ title: "Couldn't update offer" }); }
  };
  const handleStatement = async () => {
    try { await downloadLandlordStatement(); }
    catch { toast({ title: "Couldn't generate statement" }); }
  };

  const role = user.role;
  const first = user.name.split(" ")[0];
  const chargeColor = { paid: "bg-green-100 text-green-700", due: "bg-amber-100 text-amber-700", "awaiting-confirmation": "bg-blue-100 text-blue-700" };

  const roleTitle = { landlord: "Landlord Portal", tenant: "Tenant Portal", vendor: "Vendor Portal", buyer: "Buyer Portal", investor: "Investor Portal", user: "My Account", admin: "Account", agent: "Account" }[role] || "Portal";

  const tabTriggers = {
    landlord: [["overview", "Portfolio"], ["compliance", "Compliance"], ["protection", "Protection"], ["services", "Concierge"], ["photography", "Photography"], ["documents", "Documents"], ["invoices", "Invoices"], ["messages", "Messages"]],
    tenant: [["overview", "My Tenancy"], ["charges", "Rent & Charges"], ["maintenance", "Maintenance"], ["compliance", "Verification"], ["services", "Concierge"], ["documents", "Documents"], ["invoices", "Invoices"], ["messages", "Messages"]],
    vendor: [["overview", "Selling Progress"], ["services", "Concierge"], ["photography", "Photography"], ["documents", "Documents"], ["invoices", "Invoices"], ["messages", "Messages"]],
    buyer: [["overview", "Sale Progress"], ["contacts", "Contacts"], ["searches", "Saved Searches"], ["compliance", "Verification"], ["services", "Concierge"], ["documents", "Documents"], ["invoices", "Invoices"], ["messages", "Messages"]],
    investor: [["overview", "Opportunities"], ["services", "Concierge"], ["documents", "Documents"], ["messages", "Messages"]],
  }[role] || [["overview", "Overview"]];

  return (
    <div className="min-h-[70vh] bg-[#faf7f2]">
      <div className="bg-[#1c1c1e] py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2">{roleTitle}</p>
          <h1 className="font-display text-white text-[34px] md:text-[44px] font-300">Welcome, {first}</h1>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12">
        {!data ? <p className="text-center py-16 text-[#8a8a8a] font-display text-[20px]">Loading your portal...</p> : (
        <Tabs defaultValue="overview">
          <TabsList className="bg-white border border-[#f0ece5] p-1.5 mb-8 flex flex-wrap h-auto">
            {tabTriggers.map(([v, label]) => (
              <TabsTrigger key={v} value={v} className="data-[state=active]:bg-[#b8935f] data-[state=active]:text-white px-5 py-2">{label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="services"><PortalServices role={role} /></TabsContent>
          <TabsContent value="photography"><PortalPhotography /></TabsContent>

          {/* ===== LANDLORD ===== */}
          {role === "landlord" && (
            <>
              <TabsContent value="overview" className="space-y-6">
                {(() => {
                  const dueCompliance = (data.compliance || []).filter((c) => c.status === "expired" || c.status === "due-soon");
                  const outstanding = (data.invoices || []).filter((i) => i.status === "due").reduce((a, b) => a + (b.amount || 0), 0);
                  return (
                    <>
                      {dueCompliance.length > 0 && (
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="landlord-compliance-alert">
                          <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                          <div><p className="text-[14px] font-500 text-amber-800">{dueCompliance.length} compliance item{dueCompliance.length > 1 ? "s" : ""} need attention</p><p className="text-[13px] text-amber-700">{dueCompliance.map((c) => `${c.property} · ${c.type}`).join(" · ")}. Upload renewals in the Compliance tab.</p></div>
                        </div>
                      )}
                      <div className="grid sm:grid-cols-4 gap-5">
                        <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Monthly Income</p><p className="font-display text-[30px] font-500 text-[#2b2b2b]">{fmt((data.portfolio || []).filter(p => p.occupancy === "Occupied").reduce((a, b) => a + b.rent, 0))}</p></SectionCard>
                        <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Properties</p><p className="font-display text-[30px] font-500 text-[#2b2b2b]">{(data.portfolio || []).length}</p></SectionCard>
                        <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Occupancy</p><p className="font-display text-[30px] font-500 text-[#2b2b2b]">{(data.portfolio || []).filter(p => p.occupancy === "Occupied").length}/{(data.portfolio || []).length}</p></SectionCard>
                        <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Fees Outstanding</p><p className={`font-display text-[30px] font-500 ${outstanding > 0 ? "text-amber-600" : "text-[#2b2b2b]"}`}>{fmt(outstanding)}</p></SectionCard>
                      </div>
                    </>
                  );
                })()}
                <SectionCard title="My Portfolio" action={<button onClick={handleStatement} data-testid="owner-statement-download" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><FileDown size={15} /> Owner Statement</button>}>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {(data.portfolio || []).map((p) => (
                      <div key={p.id} className="rounded-xl border border-[#f0ece5] overflow-hidden">
                        <img src={p.image} alt={p.property} className="w-full h-36 object-cover" />
                        <div className="p-4">
                          <p className="font-display text-[17px] font-500 text-[#2b2b2b] mb-1">{p.property}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-[14px] text-[#b8935f] font-500">{fmt(p.rent)} pcm</span>
                            <span className={`text-[11px] px-2.5 py-1 rounded-full ${p.occupancy === "Occupied" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.occupancy}</span>
                          </div>
                          <p className="text-[12px] text-[#a0a0a0] mt-2">Tenant: {p.tenant}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </TabsContent>
              <TabsContent value="compliance"><SectionCard title="Compliance Checklist"><LandlordCompliance items={data.compliance} onRefresh={refresh} /></SectionCard></TabsContent>
              <TabsContent value="protection"><LandlordProtection /></TabsContent>
              <TabsContent value="documents"><SectionCard title="Documents"><DocumentsList documents={data.documents} /></SectionCard></TabsContent>
              <TabsContent value="invoices"><SectionCard title="Invoices"><InvoicesList invoices={data.invoices} /></SectionCard></TabsContent>
              <TabsContent value="messages"><SectionCard title="Agent Messages"><MessagesThread messages={data.messages} onSend={handleSend} /></SectionCard></TabsContent>
            </>
          )}

          {/* ===== TENANT ===== */}
          {role === "tenant" && (
            <>
              <TabsContent value="overview">
                {data.tenancy && (
                  <SectionCard title="My Tenancy" action={<button onClick={handleRenewal} data-testid="request-renewal-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><RefreshCw size={15} /> Request Renewal</button>}>
                    <div className="grid md:grid-cols-[1fr_1.2fr] gap-6">
                      <img src={data.tenancy.image} alt={data.tenancy.property} className="rounded-xl h-52 w-full object-cover" />
                      <div className="space-y-3">
                        <p className="font-display text-[22px] font-500 text-[#2b2b2b]">{data.tenancy.property}</p>
                        <div className="grid grid-cols-2 gap-3 text-[14px]">
                          <div><p className="text-[#a0a0a0] text-[12px]">Rent</p><p className="text-[#2b2b2b]">{fmt(data.tenancy.rent)} pcm</p></div>
                          <div><p className="text-[#a0a0a0] text-[12px]">Deposit</p><p className="text-[#2b2b2b]">{fmt(data.tenancy.deposit)}</p></div>
                          <div><p className="text-[#a0a0a0] text-[12px]">Start</p><p className="text-[#2b2b2b]">{fmtDate(data.tenancy.start)}</p></div>
                          <div><p className="text-[#a0a0a0] text-[12px]">End</p><p className="text-[#2b2b2b]">{fmtDate(data.tenancy.end)}</p></div>
                          <div><p className="text-[#a0a0a0] text-[12px]">Landlord</p><p className="text-[#2b2b2b]">{data.tenancy.landlord}</p></div>
                          <div><p className="text-[#a0a0a0] text-[12px]">Status</p><p className="text-green-700 capitalize">{data.tenancy.status}</p></div>
                        </div>
                      </div>
                    </div>
                  </SectionCard>
                )}
              </TabsContent>
              <TabsContent value="charges">
                <SectionCard title="Rent & Charges">
                  <div className="space-y-2">
                    {(data.charges || []).map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-4 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                        <div><p className="text-[14px] text-[#2b2b2b]">{c.label}</p><p className="text-[12px] text-[#a0a0a0]">Due {fmtDate(c.dueDate)} · {fmt(c.amount)}</p></div>
                        <div className="flex items-center gap-3">
                          <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${chargeColor[c.status] || "bg-gray-100 text-gray-600"}`}>{c.status.replace("-", " ")}</span>
                          {c.status === "due" && <button onClick={() => handlePay(c.id)} className="px-4 py-1.5 rounded-full bg-[#b8935f] text-white text-[12px] font-500 hover:bg-[#a67f4d] transition-colors">Mark as paid</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[12px] text-[#a0a0a0] mt-4">Pay rent by bank transfer, then mark it as paid — your agent will confirm receipt.</p>
                </SectionCard>
              </TabsContent>
              <TabsContent value="maintenance">
                <SectionCard title="Maintenance" action={<button onClick={() => setShowIssue((s) => !s)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> Report Issue</button>}>
                  {showIssue && (
                    <form onSubmit={handleIssue} className="mb-6 p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5] space-y-3">
                      <input value={issueForm.title} onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })} placeholder="What's the issue?" className="w-full h-[44px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
                      <textarea value={issueForm.detail} onChange={(e) => setIssueForm({ ...issueForm, detail: e.target.value })} placeholder="Add details..." rows={2} className="w-full px-4 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none" />
                      <button type="submit" className="px-5 py-2.5 rounded-lg bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d]">Submit</button>
                    </form>
                  )}
                  <div className="space-y-4">
                    {(data.issues || []).length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No maintenance issues reported.</p> :
                      (data.issues || []).map((iss) => (
                        <div key={iss.id} className="p-4 rounded-xl border border-[#f0ece5]">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-[15px] font-500 text-[#2b2b2b]">{iss.title}</p>
                            <span className="text-[11px] px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 capitalize">{iss.status.replace("-", " ")}</span>
                          </div>
                          {iss.detail && <p className="text-[13px] text-[#6a6a6a] mb-3">{iss.detail}</p>}
                          {iss.contractor && <p className="text-[12px] text-[#a0a0a0] mb-3">Contractor: {iss.contractor}</p>}
                          <div className="flex flex-wrap gap-2">
                            {(iss.timeline || []).map((t, i) => (
                              <span key={i} className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]"><Check size={12} className="text-[#b8935f]" /> {t.stage}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </SectionCard>
              </TabsContent>
              <TabsContent value="documents"><SectionCard title="Documents"><DocumentsList documents={data.documents} /></SectionCard></TabsContent>
              <TabsContent value="invoices"><SectionCard title="Invoices"><InvoicesList invoices={data.invoices} /></SectionCard></TabsContent>
              <TabsContent value="compliance"><PortalCompliance /></TabsContent>
              <TabsContent value="messages"><SectionCard title="Agent Messages"><MessagesThread messages={data.messages} onSend={handleSend} /></SectionCard></TabsContent>
            </>
          )}

          {/* ===== VENDOR ===== */}
          {role === "vendor" && data.sale && (
            <>
              <TabsContent value="overview" className="space-y-6">
                <SectionCard title="Selling Progress">
                  <div className="flex items-center gap-4 mb-6">
                    <img src={data.sale.image} alt={data.sale.property} className="w-20 h-20 rounded-xl object-cover" />
                    <div><p className="font-display text-[19px] font-500 text-[#2b2b2b]">{data.sale.property}</p><p className="text-[14px] text-[#b8935f]">Asking {fmt(data.sale.askingPrice)}</p></div>
                  </div>
                  <StageTimeline stages={data.sale.stages} current={data.sale.currentStage} />
                </SectionCard>
                <div className="grid md:grid-cols-2 gap-6">
                  <SectionCard title="Recent Viewings">
                    <div className="space-y-3">
                      {data.sale.viewings.map((v, i) => (
                        <div key={i} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]"><p className="text-[14px] text-[#2b2b2b]">{v.name}</p><p className="text-[12px] text-[#a0a0a0]">{fmtDate(v.date)}</p><p className="text-[13px] text-[#6a6a6a] mt-1">{v.feedback}</p></div>
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="Offers">
                    <div className="space-y-3">
                      {data.sale.offers.map((o, i) => (
                        <div key={i} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid="vendor-offer-row">
                          <div className="flex items-center justify-between">
                            <div><p className="text-[14px] text-[#2b2b2b]">{o.buyer}</p><p className="text-[12px] text-[#a0a0a0]">{fmtDate(o.date)}</p></div>
                            <div className="text-right"><p className="text-[15px] font-500 text-[#b8935f]">{fmt(o.amount)}</p><span className={`text-[11px] capitalize ${o.status === "accepted" ? "text-green-600" : o.status === "declined" ? "text-red-500" : "text-[#8a8a8a]"}`}>{o.status}</span></div>
                          </div>
                          {!["accepted", "declined"].includes(o.status) && (
                            <div className="flex items-center gap-2 mt-3">
                              <button onClick={() => handleOffer(i, "accept")} data-testid="offer-accept-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-[12px] font-500 hover:bg-green-700 transition-colors"><Check size={13} /> Accept</button>
                              <button onClick={() => handleOffer(i, "decline")} data-testid="offer-decline-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-red-400 hover:text-red-500 transition-colors"><X size={13} /> Decline</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                </div>
              </TabsContent>
              <TabsContent value="documents"><SectionCard title="Documents"><DocumentsList documents={data.documents} /></SectionCard></TabsContent>
              <TabsContent value="invoices"><SectionCard title="Invoices"><InvoicesList invoices={data.invoices} /></SectionCard></TabsContent>
              <TabsContent value="messages"><SectionCard title="Agent Messages"><MessagesThread messages={data.messages} onSend={handleSend} /></SectionCard></TabsContent>
            </>
          )}

          {/* ===== BUYER ===== */}
          {role === "buyer" && data.purchase && (
            <>
              <TabsContent value="overview" className="space-y-6">
                <SectionCard title="Your Purchase" action={<button onClick={handleCallback} data-testid="request-callback-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Phone size={15} /> Request Call-back</button>}>
                  <div className="flex items-center gap-4 mb-6">
                    <img src={data.purchase.image} alt={data.purchase.property} className="w-20 h-20 rounded-xl object-cover" />
                    <div><p className="font-display text-[19px] font-500 text-[#2b2b2b]">{data.purchase.property}</p><p className="text-[14px] text-[#b8935f]">Agreed {fmt(data.purchase.agreedPrice)}</p></div>
                  </div>
                  <StageTimeline stages={data.purchase.stages} current={data.purchase.currentStage} />
                </SectionCard>
              </TabsContent>
              <TabsContent value="contacts">
                <div className="grid md:grid-cols-2 gap-6">
                  <SectionCard title="Solicitor"><p className="text-[15px] text-[#2b2b2b] mb-1">{data.purchase.solicitor.name}</p><p className="text-[14px] text-[#b8935f]">{data.purchase.solicitor.contact}</p></SectionCard>
                  <SectionCard title="Mortgage Broker"><p className="text-[15px] text-[#2b2b2b] mb-1">{data.purchase.broker.name}</p><p className="text-[14px] text-[#b8935f]">{data.purchase.broker.contact}</p></SectionCard>
                </div>
              </TabsContent>
              <TabsContent value="searches">
                <SectionCard title="Saved Searches & Alerts">
                  {(data.savedSearches || []).length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No saved searches yet. Save a search from the listings page to get email alerts.</p> : (
                    <div className="space-y-3" data-testid="saved-searches">
                      {(data.savedSearches || []).map((s) => (
                        <div key={s.id} className="flex items-center justify-between p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]">
                          <div><p className="text-[14px] font-500 text-[#2b2b2b]">{s.query}</p><p className="text-[12px] text-[#a0a0a0]">Created {fmtDate(s.createdAt)}</p></div>
                          <button onClick={() => handleToggleAlerts(s.id, !s.alerts)} data-testid="alerts-toggle" className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${s.alerts ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-[#6a6a6a] border-[#e2d8c8] hover:border-[#b8935f]"}`}>{s.alerts ? "Alerts on" : "Alerts off"}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </TabsContent>
              <TabsContent value="documents"><SectionCard title="Documents"><DocumentsList documents={data.documents} /></SectionCard></TabsContent>
              <TabsContent value="invoices"><SectionCard title="Invoices"><InvoicesList invoices={data.invoices} /></SectionCard></TabsContent>
              <TabsContent value="compliance"><PortalCompliance /></TabsContent>
              <TabsContent value="messages"><SectionCard title="Agent Messages"><MessagesThread messages={data.messages} onSend={handleSend} /></SectionCard></TabsContent>
            </>
          )}

          {/* ===== INVESTOR ===== */}
          {role === "investor" && (
            <TabsContent value="overview">
              {(data.alerts || []).length > 0 && (
                <div className="bg-gradient-to-r from-[#1c1c1e] to-[#2b2b2b] rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="investor-alerts">
                  <span className="w-9 h-9 rounded-lg bg-[#b8935f]/20 flex items-center justify-center shrink-0"><TrendingUp size={18} className="text-[#d9b98a]" /></span>
                  <div className="flex-1">
                    <p className="text-[14px] font-500 text-white">{data.alerts.length} new high-yield {data.alerts.length === 1 ? "listing" : "listings"} just landed</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {data.alerts.map((a) => (
                        <Link key={a.id} to={`/property/${a.slug}`} className="text-[12px] px-3 py-1 rounded-full bg-white/10 text-white hover:bg-[#b8935f] transition-colors" data-testid="alert-chip">{a.title} · {a.grossYield}%</Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-6 mb-8" data-testid="investor-stats">
                <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Opportunities</p><p className="font-display text-[30px] font-500 text-[#2b2b2b]">{data.investorStats?.opportunities ?? 0}</p></SectionCard>
                <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Avg Gross Yield</p><p className="font-display text-[30px] font-500 text-green-600">{data.investorStats?.avgYield ?? 0}%</p></SectionCard>
                <SectionCard><p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">Watchlist</p><p className="font-display text-[30px] font-500 text-[#2b2b2b]">{data.investorStats?.watchlist ?? 0}</p></SectionCard>
              </div>
              {(data.watchlist || []).length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-3 flex items-center gap-2"><Heart size={16} className="fill-[#b8935f] text-[#b8935f]" /> Your watchlist</h3>
                  <div className="flex flex-wrap gap-2">
                    {data.watchlist.map((o) => <Link key={o.id} to={`/property/${o.slug}`} className="text-[13px] px-3 py-1.5 rounded-full bg-white border border-[#f0ece5] hover:border-[#b8935f] transition-colors" data-testid="watchlist-chip">{o.title} · {o.grossYield}%</Link>)}
                  </div>
                </div>
              )}
              <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4">Investment opportunities</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="investor-opportunities">
                {(data.opportunities || []).map((o) => (
                  <div key={o.id} className="bg-white rounded-2xl border border-[#f0ece5] overflow-hidden hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:border-[#b8935f]/40 transition-all" data-testid="investor-opp-card">
                    <div className="relative">
                      <Link to={`/property/${o.slug}`}>{o.image && <img src={o.image} alt="" className="w-full h-40 object-cover" />}</Link>
                      {o.isNew && <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-[#b8935f] text-white font-500">NEW</span>}
                      <button onClick={() => saveOpp(o.id)} data-testid="watchlist-toggle" className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${o.saved ? "bg-[#b8935f] text-white" : "bg-white/90 text-[#8a8a8a] hover:text-[#b8935f]"}`}>
                        <Heart size={15} className={o.saved ? "fill-white" : ""} />
                      </button>
                    </div>
                    <Link to={`/property/${o.slug}`} className="block p-5">
                      <p className="font-display text-[16px] font-500 text-[#2b2b2b] truncate">{o.title}</p>
                      <p className="text-[12px] text-[#a0a0a0] mb-2">{o.postcode} · {o.beds} bed</p>
                      <div className="flex items-center justify-between">
                        <div><p className="text-[15px] font-500 text-[#2b2b2b]">{fmt(o.price)}</p><p className="text-[11px] text-[#a0a0a0]">est. {fmt(o.estMonthlyRent)}/mo</p></div>
                        <div className="text-right"><p className="font-display text-[24px] font-500 text-green-600">{o.grossYield}%</p><p className="text-[11px] text-[#a0a0a0] uppercase">gross yield</p></div>
                      </div>
                    </Link>
                  </div>
                ))}
                {(data.opportunities || []).length === 0 && <p className="text-[14px] text-[#8a8a8a]">No opportunities available right now.</p>}
              </div>
            </TabsContent>
          )}

          {/* ===== DEFAULT (buyer/renter public role) ===== */}
          {["user"].includes(role) && (
            <TabsContent value="overview">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[{ icon: Heart, title: "Saved Homes", to: "/favourites" }, { icon: TrendingUp, title: "Instant Valuation", to: "/services#valuation" }, { icon: Home, title: "Browse Listings", to: "/buying" }, { icon: MessageSquare, title: "Calculators", to: "/calculators" }].map((f) => (
                  <Link key={f.title} to={f.to} className="bg-white rounded-2xl p-7 border border-[#f0ece5] hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:border-[#b8935f]/40 transition-all">
                    <span className="w-12 h-12 rounded-xl bg-[#faf7f2] flex items-center justify-center mb-4"><f.icon size={22} className="text-[#b8935f]" /></span>
                    <h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">{f.title}</h3>
                  </Link>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
        )}
      </div>
    </div>
  );
};

export default Portal;
